import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo
} from 'react'
import { AiSuggestedWorkPostSheet, WorkPostSheetItemTypesEnum } from '../types'
import { aiSuggestedWpsToWPSFormValues } from '../utils/aiSuggestedWpsToWPSFormValues'
import { isAiSuggestedWPSItem } from '../utils/isAiSuggestedWPSItem'
import { useForm, WorkPostSheetFormValues } from './FormProvider'

export type AiSuggestedWPSField = keyof AiSuggestedWorkPostSheet &
  keyof WorkPostSheetFormValues

const AI_SUGGESTED_WPS_FIELDS: AiSuggestedWPSField[] = [
  'tasksToPerform',
  'requiredQualifications',
  'workEquipment',
  'priorInstructions',
  'foreseeableTraining',
  'workstationLocation'
]

export interface AiSuggestionContextValue {
  aiSuggestedWorkPostSheet: AiSuggestedWorkPostSheet | null
  // a field / item counts as AI suggested while it still holds the value suggested by AI
  isFieldAiSuggested: (name: AiSuggestedWPSField) => boolean
  isItemAiSuggested: (name: WorkPostSheetItemTypesEnum) => boolean
  isMaternityProtectionMeasuresRequiredAiSuggested: boolean
  // at least one value in the form is still the one suggested by AI
  hasAiSuggestedValues: boolean
}

const AiSuggestionContext = createContext<AiSuggestionContextValue>({
  aiSuggestedWorkPostSheet: null,
  isFieldAiSuggested: () => false,
  isItemAiSuggested: () => false,
  isMaternityProtectionMeasuresRequiredAiSuggested: false,
  hasAiSuggestedValues: false
})

export function useAiSuggestion(): AiSuggestionContextValue {
  return useContext(AiSuggestionContext)
}

interface Props {
  aiSuggestedWorkPostSheet?: AiSuggestedWorkPostSheet | null
}

// must be rendered inside FormProvider
export const AiSuggestionProvider = ({
  aiSuggestedWorkPostSheet = null,
  children
}: PropsWithChildren<Props>) => {
  const { values } = useForm()

  const isFieldAiSuggested = useCallback(
    (name: AiSuggestedWPSField) => {
      const suggestedValue = aiSuggestedWorkPostSheet?.[name]
      return !!suggestedValue && values[name] === suggestedValue
    },
    [aiSuggestedWorkPostSheet, values]
  )

  const isItemAiSuggested = useCallback(
    (name: WorkPostSheetItemTypesEnum) =>
      isAiSuggestedWPSItem(name, values, aiSuggestedWorkPostSheet),
    [aiSuggestedWorkPostSheet, values]
  )

  const isMaternityProtectionMeasuresRequiredAiSuggested = useMemo(
    () =>
      !!aiSuggestedWorkPostSheet &&
      !!aiSuggestedWpsToWPSFormValues(aiSuggestedWorkPostSheet)
        .maternityProtectionMeasuresRequired &&
      values.maternityProtectionMeasuresRequired,
    [aiSuggestedWorkPostSheet, values.maternityProtectionMeasuresRequired]
  )

  const hasAiSuggestedValues = useMemo(
    () =>
      !values.uploadOwnWPS &&
      (AI_SUGGESTED_WPS_FIELDS.some(isFieldAiSuggested) ||
        Object.values(WorkPostSheetItemTypesEnum).some(isItemAiSuggested) ||
        isMaternityProtectionMeasuresRequiredAiSuggested),
    [
      isFieldAiSuggested,
      isItemAiSuggested,
      isMaternityProtectionMeasuresRequiredAiSuggested,
      values.uploadOwnWPS
    ]
  )

  const context = useMemo(
    () => ({
      aiSuggestedWorkPostSheet,
      isFieldAiSuggested,
      isItemAiSuggested,
      isMaternityProtectionMeasuresRequiredAiSuggested,
      hasAiSuggestedValues
    }),
    [
      aiSuggestedWorkPostSheet,
      hasAiSuggestedValues,
      isFieldAiSuggested,
      isItemAiSuggested,
      isMaternityProtectionMeasuresRequiredAiSuggested
    ]
  )

  return (
    <AiSuggestionContext.Provider value={context}>
      {children}
    </AiSuggestionContext.Provider>
  )
}
